import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import { getProductImage } from '@/lib/productMedia';

// ============================================================
// SEO Optimizer — analyse le référencement Google des fiches produits
// du marchand, calcule un score et propose une correction par IA
// (titre SEO, meta description, mots-clés) via l'Edge Function product-ai-fill.
// Données strictement limitées à la boutique connectée.
// ============================================================

interface SeoProduct {
  id: number;
  name: string;
  description: string | null;
  price: number;
  media: { url: string; type: string }[] | null;
  seo_title: string | null;
  seo_description: string | null;
  seo_keywords: string | null;
}

interface Audit {
  product: SeoProduct;
  score: number;
  issues: string[];
}

function auditProduct(p: SeoProduct): Audit {
  const issues: string[] = [];
  let score = 0;

  if (p.description && p.description.trim().length >= 80) score += 25;
  else issues.push('Description trop courte ou absente');

  if (getProductImage(p.media)) score += 15;
  else issues.push('Aucune image produit');

  if (p.seo_title && p.seo_title.trim().length >= 15 && p.seo_title.trim().length <= 70) score += 20;
  else issues.push('Titre SEO manquant ou mal calibré (55-60 car.)');

  if (p.seo_description && p.seo_description.trim().length >= 80 && p.seo_description.trim().length <= 165) score += 25;
  else issues.push('Meta description manquante ou mal calibrée (150-160 car.)');

  if (p.seo_keywords && p.seo_keywords.trim().length > 0) score += 15;
  else issues.push('Aucun mot-clé SEO');

  return { product: p, score: Math.min(100, score), issues };
}

export default function SeoOptimizerPage() {
  const { user } = useAuth();
  const [products, setProducts] = useState<SeoProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fixingId, setFixingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const fetchProducts = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('product_items')
        .select('id, name, description, price, media, seo_title, seo_description, seo_keywords')
        .eq('idcommerce', getCommerceId(user))
        .order('created_at', { ascending: false });
      if (err) throw err;
      setProducts((data || []) as SeoProduct[]);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  const audits = useMemo(() => products.map(auditProduct).sort((a, b) => a.score - b.score), [products]);
  const globalScore = audits.length > 0 ? Math.round(audits.reduce((s, a) => s + a.score, 0) / audits.length) : 0;
  const needFix = audits.filter((a) => a.score < 100);

  const handleFix = async (audit: Audit) => {
    setFixingId(audit.product.id);
    setToast(null);
    try {
      const img = getProductImage(audit.product.media);
      const { data, error: fnErr } = await supabase.functions.invoke('product-ai-fill', {
        body: { images: img ? [img] : [], name: audit.product.name, category: '', currency: '' },
      });
      if (fnErr) throw fnErr;
      const payload = data as { error?: string; result?: Record<string, string> } | null;
      if (payload?.error) throw new Error(payload.error);
      const r = payload?.result || {};
      const { error: upErr } = await supabase
        .from('product_items')
        .update({
          seo_title: r.seo_title?.slice(0, 70) || audit.product.seo_title,
          seo_description: r.seo_description?.slice(0, 165) || audit.product.seo_description,
          seo_keywords: r.seo_keywords || audit.product.seo_keywords,
          description: (audit.product.description && audit.product.description.trim().length >= 80)
            ? audit.product.description
            : (r.description || audit.product.description),
          updated_at: new Date().toISOString(),
        })
        .eq('id', audit.product.id);
      if (upErr) throw upErr;
      setToast(`« ${audit.product.name} » optimisé pour Google.`);
      fetchProducts();
    } catch (e: unknown) {
      setToast(e instanceof Error ? e.message : 'Erreur lors de la correction');
    } finally {
      setFixingId(null);
    }
  };

  const scoreColor = (s: number) => s >= 80 ? 'text-accent-600' : s >= 50 ? 'text-amber-500' : 'text-red-500';
  const scoreBg = (s: number) => s >= 80 ? 'bg-accent-500' : s >= 50 ? 'bg-amber-400' : 'bg-red-400';

  return (
    <div className="p-4 md:p-6 max-w-5xl">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-search-eye-line mr-2 text-secondary-600"></i>SEO Optimizer
        </h2>
        <p className="text-sm text-foreground-500 mt-1">Analysez et améliorez le référencement Google de vos produits</p>
      </div>

      {toast && (
        <div className="mb-5 px-4 py-3 rounded-lg text-sm font-medium bg-secondary-50 text-secondary-800 flex items-center justify-between">
          <span className="flex items-center gap-2"><i className="ri-information-line"></i>{toast}</span>
          <button onClick={() => setToast(null)} className="text-foreground-400 hover:text-foreground-700 cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}

      {/* Score global */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">Score SEO global</span>
            <i className="ri-line-chart-line text-secondary-600"></i>
          </div>
          <div className={`text-3xl font-bold font-heading ${scoreColor(globalScore)}`}>{globalScore}<span className="text-base text-foreground-300">/100</span></div>
          <div className="h-1.5 bg-background-200/70 rounded-full mt-2 overflow-hidden">
            <div className={`h-full ${scoreBg(globalScore)} rounded-full`} style={{ width: `${globalScore}%` }} />
          </div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">Produits analysés</span>
            <i className="ri-shopping-bag-3-line text-primary-500"></i>
          </div>
          <div className="text-3xl font-bold font-heading text-foreground-950">{products.length}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs text-foreground-500">À optimiser</span>
            <i className="ri-error-warning-line text-amber-500"></i>
          </div>
          <div className="text-3xl font-bold font-heading text-foreground-950">{needFix.length}</div>
        </div>
      </div>

      {/* Conseils site */}
      <div className="bg-secondary-50/60 border border-secondary-200/50 rounded-lg p-5 mb-6">
        <h3 className="text-sm font-semibold text-secondary-900 mb-2 flex items-center gap-2"><i className="ri-lightbulb-line"></i>Conseils pour être bien référencé sur Google</h3>
        <ul className="text-sm text-secondary-800/90 space-y-1">
          <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-secondary-600"></i>Remplissez le titre SEO et la meta description de chaque produit.</li>
          <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-secondary-600"></i>Ajoutez au moins une image nette par produit.</li>
          <li className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-secondary-600"></i>Rédigez une description unique et détaillée (80 caractères minimum).</li>
        </ul>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="flex items-center justify-center py-20"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : error ? (
        <div className="flex flex-col items-center py-16">
          <i className="ri-error-warning-line text-3xl text-red-400 mb-3"></i>
          <p className="text-sm text-foreground-600 mb-3">{error}</p>
          <button onClick={fetchProducts} className="px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium cursor-pointer hover:bg-primary-600">Réessayer</button>
        </div>
      ) : products.length === 0 ? (
        <div className="text-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
          <i className="ri-search-eye-line text-4xl text-foreground-300 mb-3 block"></i>
          <p className="text-sm text-foreground-500">Ajoutez des produits pour analyser leur référencement.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {audits.map((a) => (
            <div key={a.product.id} className="bg-background-50 border border-background-200/70 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-background-100 flex-shrink-0">
                {getProductImage(a.product.media) ? (
                  <img src={getProductImage(a.product.media)!} alt={a.product.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-foreground-300"><i className="ri-image-line text-xl"></i></div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h4 className="text-sm font-semibold text-foreground-900 truncate">{a.product.name}</h4>
                  <span className={`text-sm font-bold ${scoreColor(a.score)}`}>{a.score}/100</span>
                </div>
                <div className="h-1.5 bg-background-200/70 rounded-full mt-1.5 mb-2 overflow-hidden">
                  <div className={`h-full ${scoreBg(a.score)} rounded-full`} style={{ width: `${a.score}%` }} />
                </div>
                {a.issues.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {a.issues.map((iss) => (
                      <span key={iss} className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[11px]">{iss}</span>
                    ))}
                  </div>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-accent-50 text-accent-700 text-[11px]"><i className="ri-check-line"></i>Fiche optimisée</span>
                )}
              </div>
              <button
                onClick={() => handleFix(a)}
                disabled={fixingId === a.product.id}
                className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap hover:bg-primary-600 disabled:opacity-50 transition-colors cursor-pointer flex-shrink-0"
              >
                {fixingId === a.product.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-magic-line"></i>}
                {a.score === 100 ? 'Réoptimiser' : 'Optimiser par IA'}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}